import * as React from 'react';
import { useState } from 'react';
import { Link } from '@inertiajs/react';
import { FeaturedProduct } from '../../types/landing';
import { useAddToCart } from '../../hooks/useAddToCart';

interface ProductCardProps {
  product: FeaturedProduct;
}

export function ProductCard({ product }: ProductCardProps): React.ReactElement {
  const { addToCart, loading } = useAddToCart();
  const [added, setAdded] = useState(false);

  const defaultFormat = product.default_format;

  const handleAddToCart = async () => {
    if (!defaultFormat) return;
    try {
      await addToCart(product.id, defaultFormat.id, 1);
      setAdded(true);
      setTimeout(() => setAdded(false), 2000);
    } catch (e) {
      console.error('Error adding to cart:', e);
    }
  };

  const formatPrice = (price: number): string => {
    return price.toLocaleString('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  const productUrl = `/producto/${product.slug}`;

  return (
    <div className="product-card">
      <style>{`
        .product-card {
          flex: 0 0 200px;
          scroll-snap-align: start;
          background: #fff;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .product-card-link {
          text-decoration: none;
          color: inherit;
        }

        .product-card-link:hover {
          text-decoration: none;
          color: inherit;
        }

        .product-card-image {
          width: 100%;
          aspect-ratio: 1;
          object-fit: contain;
          background: #fff;
        }

        .product-card-content {
          padding: 12px;
          display: flex;
          flex-direction: column;
          flex: 1;
        }

        .product-card-category {
          font-size: 11px;
          color: #999;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 4px;
        }

        .product-card-title {
          font-size: 14px;
          font-weight: 600;
          color: #333;
          margin-bottom: 4px;
          line-height: 1.3;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          min-height: 36px;
        }

        .product-card-format {
          font-size: 12px;
          color: #666;
          margin-bottom: 8px;
        }

        .product-card-price {
          font-size: 18px;
          font-weight: 700;
          color: #5cb85c;
          margin-bottom: 12px;
        }

        .product-card-actions {
          display: flex;
          gap: 8px;
          margin-top: auto;
        }

        .product-card-view-btn {
          flex: 1;
          padding: 8px;
          font-size: 13px;
          font-weight: 500;
          color: #666;
          background: #f5f5f5;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          text-decoration: none;
          text-align: center;
          transition: all 0.2s;
        }

        .product-card-view-btn:hover {
          background: #eee;
          color: #333;
          text-decoration: none;
        }

        .product-card-add-btn {
          flex: 1;
          padding: 8px;
          font-size: 13px;
          font-weight: 600;
          color: #fff;
          background: #5cb85c;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          transition: background 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
        }

        .product-card-add-btn:hover:not(:disabled) {
          background: #4cae4c;
        }

        .product-card-add-btn:disabled {
          background: #aaa;
          cursor: not-allowed;
        }

        .product-card-add-btn.added {
          background: #28a745;
        }

        /* Mobile styles */
        @media (max-width: 768px) {
          .product-card {
            flex: 0 0 160px;
          }

          .product-card-content {
            padding: 10px;
          }

          .product-card-title {
            font-size: 13px;
            min-height: 34px;
          }

          .product-card-price {
            font-size: 16px;
            margin-bottom: 8px;
          }

          .product-card-actions {
            flex-direction: column;
            gap: 6px;
          }

          .product-card-view-btn,
          .product-card-add-btn {
            padding: 8px;
            font-size: 12px;
          }
        }
      `}</style>

      <Link href={productUrl} className="product-card-link">
        <img
          src={product.webp_image_url || product.image_url}
          alt={product.title}
          className="product-card-image"
          loading="lazy"
        />
      </Link>

      <div className="product-card-content">
        <div className="product-card-category">{product.category_name}</div>
        <Link href={productUrl} className="product-card-link">
          <div className="product-card-title">{product.title}</div>
        </Link>

        {defaultFormat && (
          <div className="product-card-format">{defaultFormat.name}</div>
        )}

        <div className="product-card-price">
          {defaultFormat ? formatPrice(defaultFormat.price) : '-'}
        </div>

        <div className="product-card-actions">
          <Link href={productUrl} className="product-card-view-btn">
            Ver
          </Link>
          <button
            type="button"
            className={`product-card-add-btn ${added ? 'added' : ''}`}
            onClick={handleAddToCart}
            disabled={loading || !defaultFormat}
          >
            {loading ? (
              <i className="fa fa-spinner fa-spin"></i>
            ) : added ? (
              <>
                <i className="fa fa-check"></i>
              </>
            ) : (
              <>
                <i className="fa fa-cart-plus"></i>
                Agregar
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
